/**
 * Ladicí panel pro posazení předmětů v ruce.
 *
 * Posazení modelu na kost se nedá spočítat — musí se vidět. Dokud se ladilo
 * přepisováním konstant, znamenala každá změna kolo build → nasazení →
 * podívej se. Tady se táhne za posuvník a výsledek je vidět okamžitě;
 * na konci tlačítko vysype hotový blok kódu ke vložení do zdrojáku.
 *
 * Je to vývojářský nástroj, ne herní funkce — proto je schovaný pod klávesou
 * a v hotové hře se prostě nezapne.
 */

export interface FitField {
  key: string;
  label: string;
  min: number;
  max: number;
  step: number;
  /** Krok pro číselné pole a tlačítka. Když chybí, použije se `step`. */
  fine?: number;
  value: number;
  /**
   * Nadpis skupiny, do které pole patří.
   *
   * Uvádí se jen u prvního pole skupiny. Bez rozdělení není z panelu
   * poznat, co ovlivňuje kostru a co jen tenhle jeden model.
   */
  section?: string;
  /** Vysvětlivka pod nadpisem skupiny. */
  hint?: string;
}

export interface FitTarget {
  /** Nadpis sekce. */
  title: string;
  fields: FitField[];
  /** Zavolá se při každé změně; vrací blok kódu k vypsání. */
  apply: (values: Record<string, number>) => string;
  /** Panel se otevřel nebo zavřel — vhodné pozastavit ovládání. */
  onToggle?: (open: boolean) => void;
}

export class FitPanel {
  private readonly root: HTMLElement;
  private readonly output: HTMLElement;
  private readonly values: Record<string, number> = {};
  private onToggle?: (open: boolean) => void;
  private open = false;

  constructor(
    parent: HTMLElement,
    target: FitTarget,
    private readonly toggleKey = 'F9',
  ) {
    this.root = document.createElement('div');
    this.root.id = 'fit-panel';
    this.root.innerHTML = `
      <header>
        <strong>${escapeHtml(target.title)}</strong>
        <span>${toggleKey} zavře</span>
      </header>
      <div class="fit-rows"></div>
      <button type="button" class="fit-copy">Zkopírovat hodnoty</button>
      <pre class="fit-out"></pre>
    `;
    parent.appendChild(this.root);

    const rows = this.root.querySelector('.fit-rows')!;
    this.output = this.root.querySelector('.fit-out')!;

    for (const field of target.fields) {
      this.values[field.key] = field.value;

      if (field.section) {
        const heading = document.createElement('div');
        heading.className = 'fit-section';
        heading.innerHTML =
          `<strong>${escapeHtml(field.section)}</strong>` +
          (field.hint ? `<span>${escapeHtml(field.hint)}</span>` : '');
        rows.appendChild(heading);
      }

      const row = document.createElement('div');
      row.className = 'fit-row';
      // Posuvník na hrubé hledání, číslo na přesné dostavení, šipky na
      // jemný krok. Samotný posuvník je na desetiny stupně moc hrubý.
      row.innerHTML = `
        <span class="fit-label">${escapeHtml(field.label)}</span>
        <input class="fit-slider" type="range" min="${field.min}" max="${field.max}"
               step="${field.step}" value="${field.value}" />
        <button class="fit-step" data-dir="-1" type="button" title="jemně dolů">−</button>
        <input class="fit-num" type="number" min="${field.min}" max="${field.max}"
               step="${field.fine ?? field.step}" value="${field.value}" />
        <button class="fit-step" data-dir="1" type="button" title="jemně nahoru">+</button>
      `;
      const slider = row.querySelector<HTMLInputElement>('.fit-slider')!;
      const number = row.querySelector<HTMLInputElement>('.fit-num')!;
      const fine = field.fine ?? field.step;

      const set = (value: number) => {
        const clamped = Math.min(field.max, Math.max(field.min, value));
        // Zaokrouhlení na krok, jinak by z posuvníku lezla čísla
        // jako 1.1500000000000001.
        const snapped = Number((Math.round(clamped / fine) * fine).toFixed(6));
        this.values[field.key] = snapped;
        slider.value = String(snapped);
        number.value = String(snapped);
        this.output.textContent = target.apply({ ...this.values });
      };

      slider.oninput = () => set(Number(slider.value));
      number.oninput = () => set(Number(number.value));
      for (const button of row.querySelectorAll<HTMLButtonElement>('.fit-step')) {
        button.onclick = () => set(this.values[field.key] + fine * Number(button.dataset.dir));
      }
      rows.appendChild(row);
    }

    this.root.querySelector<HTMLButtonElement>('.fit-copy')!.onclick = () => {
      const text = this.output.textContent ?? '';
      void navigator.clipboard?.writeText(text);
      const button = this.root.querySelector<HTMLButtonElement>('.fit-copy')!;
      button.textContent = 'Zkopírováno';
      window.setTimeout(() => (button.textContent = 'Zkopírovat hodnoty'), 1200);
    };

    this.onToggle = target.onToggle;
    this.output.textContent = target.apply({ ...this.values });
    this.setOpen(false);
    window.addEventListener('keydown', this.onKeyDown);
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    this.root.remove();
  }

  private onKeyDown = (event: KeyboardEvent): void => {
    if (event.code !== this.toggleKey) return;
    event.preventDefault();
    this.setOpen(!this.open);
  };

  private setOpen(open: boolean): void {
    this.open = open;
    this.root.classList.toggle('visible', open);

    // Při otevřeném panelu se musí uvolnit kurzor, jinak by hráč táhl
    // za posuvník naslepo a myš by mu mezitím točila kamerou.
    if (open) document.exitPointerLock?.();

    // Uvolněný kurzor ale vyvolá výzvu "Klikni pro ovládání", která scénu
    // ztmaví a rozmaže. Při ladění posazení je to to poslední, co chceme —
    // právě na tu postavu se potřebujeme dívat ostře.
    document.getElementById('click')?.classList.toggle('fit-hidden', open);
    this.onToggle?.(open);
  }
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);
}
